import { useState, useRef, useEffect } from "react";
import { Input, Popover, Button } from "antd";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IconLineEdit } from "@kalodata/kds-design-icons";
import { Guide } from "@pages/video/components/ImageUpload/MultiImageUpload/Guide";
import { IMAGE_CAPTION_MAX_LENGTH } from "./types";

const TAG_OPTIONS = ["Front", "Side", "Back", "Top", "Bottom", "Lifestyle"] as const;

interface PopoverContentProps {
  currentValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}

const PopoverContent = ({ currentValue, onSave, onCancel }: PopoverContentProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<any>(null);
  const [value, setValue] = useState(currentValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus({ cursor: "end" });
  }, []);

  const validate = (val: string) => {
    const trimmed = val.trim();
    if (!trimmed) return t("This field is required");
    if (trimmed.length > IMAGE_CAPTION_MAX_LENGTH) {
      return t("Tag must be {{max}} characters or fewer", { max: IMAGE_CAPTION_MAX_LENGTH });
    }
    return null;
  };

  const handleTagClick = (tag: string) => {
    const translated = t(tag);
    setValue(translated);
    setError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (error) setError(validate(e.target.value));
  };

  const handleSave = () => {
    const err = validate(value);
    if (err) {
      setError(err);
      return;
    }
    onSave(value.trim());
  };

  const matchedTag = TAG_OPTIONS.find((tag) => t(tag).toLowerCase() === value.trim().toLowerCase()) ?? null;

  return (
    <div className="flex flex-col gap-[8px] w-[400px]">
      <div className="p-[12px] flex flex-col gap-[8px]">
        <div className="flex flex-col gap-[4px]">
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onPressEnter={handleSave}
            placeholder={t("Front")}
            maxLength={IMAGE_CAPTION_MAX_LENGTH}
            status={error ? "error" : undefined}
            className="h-[32px] bg-kds-bg-element rounded-[8px] text-[14px] text-kds-fg border-solid"
          />
          {error && <span className="text-[12px] leading-[16px] text-kds-danger-fg">{error}</span>}
        </div>
        <div className="flex flex-wrap gap-[8px]">
          {TAG_OPTIONS.map((tag) => {
            const selected = matchedTag === tag;
            return (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagClick(tag)}
                className={classNames(
                  "px-[10px] py-[4px] rounded-full border border-solid text-[14px] font-medium leading-[20px] cursor-pointer transition-colors",
                  selected
                    ? "bg-kds-accent-soft border-kds-accent-border text-kds-accent-fg"
                    : "bg-kds-bg-element border-kds-border-subtle text-kds-fg-soft hover:border-kds-accent-border hover:text-kds-accent-fg",
                )}
              >
                {t(tag)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between items-center border-t border-kds-border px-[16px] py-[12px]">
        <Guide />
        <div className="flex items-center gap-[8px]">
          <Button size="middle" onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button type="primary" size="middle" onClick={handleSave}>
            {t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export interface ImageCaptionPopoverProps {
  value?: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  /** 挂载到 Modal 等容器内，避免浮层定位到屏幕左上角 */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

/**
 * 图片 caption 编辑 popover。设计稿节点：3615:16918
 *
 * 支持快速标签 (Front/Side/Back/Top/Bottom/Lifestyle)、自定义输入、Guide 说明链接。
 */
const defaultGetPopupContainer = (trigger: HTMLElement): HTMLElement =>
  (trigger.closest("[data-image-caption-anchor]") as HTMLElement | null) ??
  (trigger.closest(".ant-modal-wrap") as HTMLElement | null) ??
  document.body;

export const ImageCaptionPopover = ({ value, onChange, children, getPopupContainer }: ImageCaptionPopoverProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      arrow={false}
      placement="bottomLeft"
      align={{ offset: [0, 4] }}
      autoAdjustOverflow={false}
      destroyOnHidden
      getPopupContainer={getPopupContainer ?? defaultGetPopupContainer}
      content={
        <PopoverContent
          currentValue={value ?? ""}
          onSave={(newValue) => {
            onChange(newValue);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      }
    >
      <div>{children}</div>
    </Popover>
  );
};

interface CaptionDisplayProps {
  caption?: string;
  onClick?: () => void;
}

/** 缩略图下方的 caption 显示（含 edit 图标），点击触发 popover */
export const CaptionDisplay = ({ caption, onClick }: CaptionDisplayProps) => {
  const { t } = useTranslation();
  const isEmpty = !caption || caption.trim() === "";

  return (
    <div
      role="button"
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      className="flex w-full items-center justify-between gap-[6px] py-[6px] cursor-pointer text-kds-fg-soft hover:text-kds-fg"
    >
      <span
        className={classNames(
          "flex-1 min-w-0 truncate text-center text-[14px] leading-[1.4]",
          isEmpty ? "text-kds-fg-subtle" : "text-kds-fg-soft",
        )}
      >
        {isEmpty ? `+ ${t("Caption")}` : caption}
      </span>
      <IconLineEdit size={16} className="shrink-0" />
    </div>
  );
};
